import * as React from 'react'

import { IReactionDisposer, reaction } from 'mobx'
import { inject, observer } from 'mobx-react'

import DesktopFileInput from '~/client/src/desktop/components/FileInput/DesktopFileInput'
import DesktopEventStore from '~/client/src/desktop/stores/EventStore/DesktopEvents.store'
import { withErrorBoundary } from '~/client/src/shared/components/ErrorBoundary'
import { Loader } from '~/client/src/shared/components/Loader'
import UnsafeSitePermitCreationFormContent from '~/client/src/shared/components/SitePermitCreationForm/SitePermitCreationFormContent'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import ClosuresStore from '~/client/src/shared/stores/domain/Closures.store'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import MarkupsStore from '~/client/src/shared/stores/domain/Markups.store'
import MaterialCategoriesStore from '~/client/src/shared/stores/domain/MaterialCategories.store'
import MaterialsStore from '~/client/src/shared/stores/domain/Materials.store'
import OperationRulesStore from '~/client/src/shared/stores/domain/OperationRules.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import SmartFormImportManagerStore from '~/client/src/shared/stores/domain/SmartFormImportManager.store'
import TagsStore from '~/client/src/shared/stores/domain/Tags.store'
import UserProjectsStore from '~/client/src/shared/stores/domain/UserProjects.store'
import WorkflowCategoriesStore from '~/client/src/shared/stores/domain/WorkflowCategories.store'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import { WORKFLOW_TYPES_RECEIVED } from '~/client/src/shared/stores/EventStore/eventConstants'
import EventContext from '~/client/src/shared/stores/EventStore/EventContext'
import PermitBallInCourtStore from '~/client/src/shared/stores/ui/PermitBallInCourt.store'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'
import { NOOP } from '~/client/src/shared/utils/noop'

import { Wrapper } from '../SetupPageLayout/SetupPageLayout'
import FieldMappingScreen from './components/FieldMappingScreen/FieldMappingScreen'
import FormTypeConfigurator from './components/FormTypeConfigurator/FormTypeConfigurator'
import FormTypesList from './components/FormTypesList'
import UnsafeNewFormTypeDialog from './components/NewFormTypeDialog'
import PermitsSetUpStore from './PermitsSetUp.store'
import PreviewPermitFormStore from './PreviewPermitForm.store'

import './PermitsSetUp.scss'

// localization: translated

const SitePermitCreationFormContent = withErrorBoundary(
  UnsafeSitePermitCreationFormContent,
)
const NewFormTypeDialog = withErrorBoundary(UnsafeNewFormTypeDialog)

interface IProps {
  eventsStore?: DesktopEventStore
  projectDateStore?: ProjectDateStore
  companiesStore?: CompaniesStore
  workflowTypesStore?: WorkflowTypesStore
  operationRulesStore?: OperationRulesStore
  userProjectsStore?: UserProjectsStore
  projectMembersStore?: ProjectMembersStore
  locationAttributesStore?: LocationAttributesStore
  materialsStore?: MaterialsStore
  materialCategoryStore?: MaterialCategoriesStore
  tagsStore?: TagsStore
  workflowCategoriesStore?: WorkflowCategoriesStore
  closuresStore?: ClosuresStore
  markupsStore?: MarkupsStore
  smartFormImportManagerStore?: SmartFormImportManagerStore
}

@inject(
  'eventsStore',
  'projectDateStore',
  'companiesStore',
  'workflowTypesStore',
  'operationRulesStore',
  'userProjectsStore',
  'projectMembersStore',
  'locationAttributesStore',
  'materialsStore',
  'materialCategoryStore',
  'tagsStore',
  'workflowCategoriesStore',
  'closuresStore',
  'markupsStore',
  'smartFormImportManagerStore',
)
@observer
export default class PermitsSetUp extends React.Component<IProps> {
  private readonly store: PermitsSetUpStore
  private readonly previewPermitStore: PreviewPermitFormStore
  private readonly permitBicStore: PermitBallInCourtStore
  private readonly disposeDraftReaction: IReactionDisposer

  private readonly clearPostEventCallback: () => void = NOOP

  public constructor(props: IProps) {
    super(props)

    this.previewPermitStore = new PreviewPermitFormStore(
      props.eventsStore,
      props.projectDateStore,
      null,
      props.workflowTypesStore,
      props.userProjectsStore,
      props.projectMembersStore,
      props.companiesStore,
      props.locationAttributesStore,
      props.materialsStore,
      props.materialCategoryStore,
      props.tagsStore,
      null,
      props.closuresStore,
      props.markupsStore,
    )

    this.store = new PermitsSetUpStore(
      props.eventsStore,
      props.workflowTypesStore,
      props.workflowCategoriesStore,
      this.previewPermitStore,
    )

    this.permitBicStore = new PermitBallInCourtStore(
      props.eventsStore,
      props.operationRulesStore,
      props.projectMembersStore,
      props.workflowTypesStore,
      props.companiesStore,
    )

    this.clearPostEventCallback = props.eventsStore.addPostEventCallback(
      this.onPermitTypesReceived,
    )

    this.disposeDraftReaction = reaction(
      () => props.smartFormImportManagerStore.pendingDraft,
      pendingDraft => {
        if (pendingDraft) {
          const draft = props.smartFormImportManagerStore.consumePendingDraft()
          this.store.setDraftPermitType(draft)
        }
      },
      { fireImmediately: true },
    )
  }

  public componentWillUnmount() {
    this.clearPostEventCallback()
    this.disposeDraftReaction()
  }

  public render() {
    if (!this.store.isReceived) {
      return (
        <Loader
          className="full-height col x-center y-center"
          hint={Localization.translator.loading}
        />
      )
    }

    return (
      <div className="row full-height permit-settings">
        <Wrapper className="overflow-hidden">
          <div className="row full-height y-start relative">
            {this.store.isUpdating && (
              <div className="full-height full-width absolute modal-overlay">
                <Loader
                  className="col x-center y-center full-height"
                  size="lg"
                  hint={`${Localization.translator.templateUpdating}...`}
                />
              </div>
            )}

            <div className="col full-height full-width relative">
              {this.content}
            </div>
          </div>
        </Wrapper>
        {this.formPreview}
      </div>
    )
  }

  private get content(): JSX.Element {
    const {
      activePermitType,
      isUpdating,
      isNewFormTypeDialogShown,
      changeShowInApp,
      showNewFormTypeDialog,
      removeCustomPermitTypes,
      setActivePermitTypeId,
      saveFormType,
      resetSelectedFormType,
    } = this.store

    if (!activePermitType) {
      return (
        <>
          {isNewFormTypeDialogShown && <NewFormTypeDialog store={this.store} />}
          <div className="col">
            <FormTypesList
              isUpdating={isUpdating}
              onSelectType={setActivePermitTypeId}
              onShowInAppChange={changeShowInApp}
              onAddNewFormClick={showNewFormTypeDialog}
              onRemoveTypes={removeCustomPermitTypes}
            />
          </div>
        </>
      )
    }

    if (this.store.isDraft && this.store.isFieldMappingPhase) {
      return (
        <FieldMappingScreen
          draftType={activePermitType}
          onComplete={this.store.completeFieldMapping}
          onBack={resetSelectedFormType}
          onPreviewUpdate={this.previewPermitStore.setPreviewTemplate}
        />
      )
    }

    return (
      <FormTypeConfigurator
        formType={activePermitType}
        isUpdating={isUpdating}
        isDraft={this.store.isDraft}
        onBackClick={resetSelectedFormType}
        saveFormType={saveFormType}
        permitBicStore={this.permitBicStore}
        onTemplateUpdate={this.previewPermitStore.setPreviewTemplate}
      />
    )
  }

  private get formPreview(): JSX.Element {
    if (!this.store.activePermitType) return null

    return (
      <div className="no-grow permit-form-preview full-height px20 pt20 col relative">
        <div className="text white extra-large center mb20">
          {Localization.translator.workflowPreview}
        </div>
        <div className="full-height full-width overflow-hidden relative">
          <SitePermitCreationFormContent
            isTypeReadOnly
            isPreviewMode
            store={this.previewPermitStore}
            isReadonly={false}
            FileInputType={DesktopFileInput}
          />
        </div>
      </div>
    )
  }

  private onPermitTypesReceived = (eventContext: EventContext) => {
    const [eventType] = eventContext.event

    if (eventType === WORKFLOW_TYPES_RECEIVED) {
      this.store.resetSelectedFormIfNeed()
    }
  }
}
